import React, { useLayoutEffect, useRef, useEffect, type ReactNode } from 'react';
import { graphql } from 'gatsby';
import useMediaQuery from '@/hooks/useMediaQuery';
import { breakpoints } from '@/constants/breakpoints';
import {
  StyledMenuItem,
  StyledMenuItemSecondary,
  StyledTitle,
  StyledBorder,
  StyledButton,
  StyledIcon,
  StyledContent,
  StyledColumns,
  StyledColumn,
} from './MenuItem.styles';
import ColumnSection from './components/ColumnSection/ColumnSection';
import { throttle } from '@/utils/throttle';
import { useMenuContext } from '../../contexts/MenuContext';

interface MenuItemProps extends Queries.HEADER_MENU_ITEMFragment {
  onLinkClick: () => void;
  isHighlighted?: boolean;
  ctaSection?: ReactNode;
  isActive?: boolean;
  onActivate?: () => void;
}

const MenuItem = ({
  system,
  elements,
  ctaSection: ctaSectionProp,
  isActive: isActiveProp,
  onActivate: onActivateProp,
  onLinkClick,
  isHighlighted: isHighligthed,
}: MenuItemProps) => {
  const { title, columns } = elements;
  const { activeMenuItemId, setActiveMenuItemId, menuButtonsRef, dropdownContentsRef } =
    useMenuContext();

  // Use props if provided, otherwise use context state
  const isActive = isActiveProp ?? activeMenuItemId === system.id;
  const onActivate = onActivateProp || (() => setActiveMenuItemId(isActive ? null : system.id));
  const ctaSection = ctaSectionProp || <></>;

  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.DESKTOP}px)`);

  const menuItemRef = useRef<HTMLButtonElement | null>(null);
  const menuDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const menuButton = menuItemRef.current;
    const dropdownContent = menuDropdownRef.current;
    const menuButtonsSet = menuButtonsRef?.current;
    const dropdownContentsSet = dropdownContentsRef?.current;

    if (menuButton && menuButtonsSet) {
      menuButtonsSet.add(menuButton);
    }
    if (dropdownContent && dropdownContentsSet) {
      dropdownContentsSet.add(dropdownContent);
    }

    return () => {
      if (menuButton && menuButtonsSet) {
        menuButtonsSet.delete(menuButton);
      }
      if (dropdownContent && dropdownContentsSet) {
        dropdownContentsSet.delete(dropdownContent);
      }
    };
  }, [menuButtonsRef, dropdownContentsRef]);

  useLayoutEffect(() => {
    const throttledHandleResize = throttle(() => {
      const menuItemElement = menuItemRef.current;
      const dropdownElement = menuDropdownRef.current;
      const paddingRight = 30; // 30px padding right so that the dropdown is not too close to the right side of the viewport.

      if (window.innerWidth < breakpoints.DESKTOP) {
        // Reset the dropdown position in the small viewport.
        if (dropdownElement) {
          dropdownElement.style.left = 'unset';
        }
        return;
      }

      if (menuItemElement) {
        const menuItemLeft = menuItemElement.getBoundingClientRect().left;
        const dropdownWidth = dropdownElement!.offsetWidth;
        const viewportWidth = window.innerWidth;
        const offsetLeft = 28; // 28px offset to the left side of the menu item to vertically align with the text on the dropdown.
        dropdownElement!.style.left = `${menuItemLeft - offsetLeft}px`;
        const viewportWidthNeededForDropdown = menuItemLeft + dropdownWidth + paddingRight;
        const dropdownIsOverflowingViewport = viewportWidthNeededForDropdown > viewportWidth;

        if (dropdownIsOverflowingViewport) {
          const overflow = viewportWidthNeededForDropdown - viewportWidth;
          dropdownElement!.style.left = `${menuItemLeft - offsetLeft - overflow}px`;
        }
      }
    });

    throttledHandleResize();
    window.addEventListener('resize', throttledHandleResize);

    const setDropdownWidth = () => {
      const waitForDropdownToRender = 350;

      const isLargeViewport = window.innerWidth >= breakpoints.DESKTOP;
      if (isLargeViewport) {
        setTimeout(throttledHandleResize, waitForDropdownToRender);
      }
    };

    setDropdownWidth();
    window.addEventListener('resize', setDropdownWidth);

    return () => {
      window.removeEventListener('resize', throttledHandleResize);
      window.removeEventListener('resize', setDropdownWidth);
    };
  }, []);

  return (
    <>
      <StyledButton onClick={onActivate} ref={menuItemRef}>
        <StyledMenuItem
          isActive={isActive}
          isHighlighted={isHighligthed ?? false}
          isDesktop={isDesktop}
        >
          <StyledTitle>{title.value}</StyledTitle>
          <StyledIcon name="chevron-blue-down" />
        </StyledMenuItem>
      </StyledButton>
      <StyledContent isActive={isActive} ref={menuDropdownRef} role="menu">
        {!isDesktop && (
          <StyledMenuItemSecondary
            isActive={isActive}
            isHighlighted={isHighligthed ?? false}
            onClick={onActivate}
            isDesktop={isDesktop}
          >
            <StyledTitle>{title.value}</StyledTitle>
            <StyledIcon name="bxs-chevron-down" />
          </StyledMenuItemSecondary>
        )}
        <StyledColumns>
          {columns.value.map((column) => (
            <React.Fragment key={column.system.id}>
              <StyledColumn
                size={column.elements.size?.value}
                isHighlighted={column.elements.options?.value?.includes('is_highlighted') ?? false}
              >
                {column.elements.sections.value.map((section) => (
                  <ColumnSection {...section} key={section.system.id} onLinkClick={onLinkClick} />
                ))}
              </StyledColumn>
              {column.elements.options?.value?.includes('divider') && <StyledBorder />}
            </React.Fragment>
          ))}
        </StyledColumns>
        {!isDesktop && ctaSection}
      </StyledContent>
    </>
  );
};

export const fragment = graphql`
  fragment HEADER_MENU_ITEM_COLUMN on kontent_item_header_menu_item_column {
    system {
      id
    }
    elements {
      options {
        value
      }
      size {
        value
      }
      sections {
        value {
          ...HEADER_MENU_ITEM_COLUMN_SECTION
        }
      }
    }
  }

  fragment HEADER_MENU_ITEM on kontent_item_header_menu_item {
    elements {
      title {
        value
      }
      columns {
        value {
          ...HEADER_MENU_ITEM_COLUMN
        }
      }
    }
    system {
      id
    }
  }
`;

export default MenuItem;
